import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaCrown,
  FaCheck,
  FaInfoCircle,
  FaTimes,
  FaMagic,
} from "react-icons/fa";
import * as Dialog from "@radix-ui/react-dialog";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Spinner } from "react-bootstrap";


interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
}

interface SubscriptionStatus {
  plan: string;
  limits: {
    emails_per_day: number;
    campaigns_per_month: number;
    leads_per_month: number;
  };
}

interface UpgradeModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  title?: string;
  alertText?: string;
  statsText?: string;
  footerText?: string;
  trigger?: React.ReactNode;
  onTriggerClick?: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  alertText = "Unlock unlimited potential and grow your business with our premium features.",
  trigger,
  onTriggerClick,
}) => {
  const navigate = useNavigate();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    } else {
      if (!isControlled) {
        setInternalIsOpen(true);
      }
    }
  };

  const handleClose = () => {
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (onTriggerClick) {
      onTriggerClick();
    } else if (!isControlled) {
      setInternalIsOpen(true);
    }
  };
  const [processing, setProcessing] = useState(false);
  const [currentSub, setCurrentSub] = useState<SubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        axios.get("/api/subscription/plans"),
        axios.get("/api/subscription/current"),
      ]);
      setPlans(plansRes.data);
      setCurrentSub(subRes.data);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const currentPlanId = currentSub?.plan?.toLowerCase() || "free";

  // Determine next plan
  let nextPlan: Plan | undefined;
  if (currentPlanId === "free") {
    nextPlan = plans.find((p) => p.id === "pro");
  } else if (currentPlanId === "pro") {
    nextPlan = plans.find((p) => p.id === "enterprise");
  } else {
    // Already on top plan or unknown
    nextPlan =
      plans.find((p) => p.id === "enterprise") || plans[plans.length - 1];
  }

  const handleUpgrade = async () => {
    if (!nextPlan) {
      navigate("/subscription");
      handleClose();
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post("/api/subscription/checkout", {
        planId: nextPlan.id,
        successUrl: window.location.origin + "/subscription?success=true",
        cancelUrl: window.location.origin + "/subscription?canceled=true",
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      const msg =
        error?.response?.data?.error || "Failed to start upgrade process";
      toast.error(msg);
      setProcessing(false);
    }
  };

  const handleSeePlans = () => {
    navigate("/subscription");
    handleClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      {trigger && (
        <Dialog.Trigger asChild onClick={handleTriggerClick}>
          {trigger}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[555555] bg-black/40 backdrop-blur-sm" />
        <Dialog.Content asChild>
          <div className="fixed left-1/2 top-1/2 z-[555555] w-full max-w-[900px] -translate-x-1/2 -translate-y-1/2 focus:outline-none px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-[24px] overflow-hidden shadow-2xl flex flex-col relative min-h-[500px]"
            >
              {loading ? (
                <div className="flex-1 flex items-center justify-center py-20">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  {/* Alert Banner */}
                  <div className="bg-[#ffe6e6] border border-[#ff0000] m-4 p-3 rounded-[10px] flex items-center gap-3 text-[#17150b] text-xs relative">
                    <FaInfoCircle className="text-lg shrink-0" />
                    <span className="flex-1 font-medium">{alertText}</span>
                    <Dialog.Close className="p-1 hover:bg-[#ffe4e6] rounded transition-colors text-[#17150b]">
                      <FaTimes className="text-xl" />
                    </Dialog.Close>
                  </div>

                  <div className="flex flex-col md:flex-row p-8 gap-8 md:gap-12">
                    {/* Left Section */}
                    <div className="flex-[1.2] flex flex-col items-center text-center pt-8">
                      <FaCrown className="text-[3.5rem] text-[#ffc107] mb-6 drop-shadow-[0_4px_6px_rgba(255,193,7,0.3)]" />
                      <Dialog.Title className="text-2xl font-semibold text-[#0f0f0f] mb-4">
                        Upgrade to {nextPlan?.name || "Pro"}
                      </Dialog.Title>
                      <p className="text-[#7d7b78] text-sm mb-4 max-w-[320px]">
                        Unlock more power with the {nextPlan?.name} plan. Scale
                        your outreach and maximize your growth.
                      </p>
                      <p className="text-[#7d7b78] text-sm mb-10 max-w-[380px]">
                        The system will start processing your upgrade securely
                        via Stripe. You'll be redirected back once complete.
                      </p>

                      <div className="flex items-center gap-8 w-full justify-center">
                        <button
                          className="text-[#17150b] font-semibold text-sm hover:opacity-70 transition-opacity"
                          onClick={handleSeePlans}
                        >
                          See More Plans
                        </button>
                        <button
                          className="bg-[#006eff] min-w-[237px] text-white px-8 py-3 rounded-lg font-semibold text-sm flex justify-center items-center gap-3 hover:bg-[#0056cc] transition-all active:scale-[0.98] disabled:opacity-70"
                          onClick={handleUpgrade}
                          disabled={processing}
                        >
                          {processing ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <>
                              <FaCrown className="text-[#ffc107]" /> Upgrade to{" "}
                              {nextPlan?.name || "Pro"}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Right Section */}
                    <div className="flex-[0.8] bg-[#f8f8f8] rounded-[14px] p-8 flex flex-col">
                      <h3 className="text-sm font-semibold text-[#17150b] mb-6">
                        Benefits include:
                      </h3>
                      <ul className="flex flex-col gap-5 mb-12">
                        {(
                          nextPlan?.features || [
                            "Higher Email Limits",
                            "Priority Support",
                            "Advanced Campaigns",
                            "Detailed Analytics",
                            "Custom Branding",
                          ]
                        ).map((benefit) => (
                          <li
                            key={benefit}
                            className="flex items-center gap-3 text-sm text-[#17150b]"
                          >
                            <FaCheck className="text-[#006eff] text-sm" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>

                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UpgradeModal;
